import React, { useState } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  Grid,
  TextField,
  Card,
  CardContent,
  InputAdornment,
  Alert,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Tooltip,
} from '@mui/material';
import { 
  Search, 
  Refresh,
  AccountBalance,
  Info,
  CheckCircle,
  Warning,
  CalendarToday,
  Receipt,
  Person,
  AttachMoney,
  Business,
  Description,
  GetApp,
  Print,
  Download
} from '@mui/icons-material';

const PayoutReportForBank = () => {
  // Search form state
  const [searchForm, setSearchForm] = useState({
    fromDate: '',
    toDate: '',
    payoutNo: '',
    memberId: '',
  });
  
  const [successMessage, setSuccessMessage] = useState('');
  const [errors, setErrors] = useState({});
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Sample bank payout data
  const [bankPayoutData] = useState([
    {
      id: 1,
      payoutNo: 'BP001',
      memberId: 'M001',
      memberName: 'John Smith',
      accountNumber: '1234567890',
      bankName: 'State Bank of India',
      ifscCode: 'SBIN0001234',
      netAmount: 15000,
      tdsAmount: 1500,
      grossAmount: 16500,
      paymentDate: '2024-01-15',
      status: 'Processed',
      transactionId: 'TXN001234567',
    },
    {
      id: 2,
      payoutNo: 'BP002',
      memberId: 'M002',
      memberName: 'Sarah Johnson',
      accountNumber: '0987654321',
      bankName: 'HDFC Bank',
      ifscCode: 'HDFC0000987',
      netAmount: 22000,
      tdsAmount: 2200,
      grossAmount: 24200,
      paymentDate: '2024-01-20',
      status: 'Pending',
      transactionId: 'TXN002345678',
    },
    {
      id: 3,
      payoutNo: 'BP003',
      memberId: 'M003',
      memberName: 'Michael Brown',
      accountNumber: '1122334455',
      bankName: 'ICICI Bank',
      ifscCode: 'ICIC0001122',
      netAmount: 18500,
      tdsAmount: 1850,
      grossAmount: 20350,
      paymentDate: '2024-01-25',
      status: 'Failed',
      transactionId: 'TXN003456789',
    },
  ]);

  const handleSearchFormChange = (field, value) => {
    setSearchForm(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear errors when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Validate date range if both dates are provided
    if (searchForm.fromDate && searchForm.toDate) {
      const fromDate = new Date(searchForm.fromDate);
      const toDate = new Date(searchForm.toDate);
      
      if (fromDate > toDate) {
        newErrors.toDate = 'To date cannot be earlier than From date';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSearch = () => {
    if (!validateForm()) {
      return;
    }

    console.log('Searching bank payout report with data:', searchForm);
    setSuccessMessage(`Bank payout report generated! Found ${bankPayoutData.length} payout record(s) for bank processing.`);
    
    // Clear success message after 3 seconds
    setTimeout(() => {
      setSuccessMessage('');
    }, 3000);
  };

  const handleReset = () => {
    setSearchForm({
      fromDate: '',
      toDate: '',
      payoutNo: '',
      memberId: '',
    });
    setErrors({});
    setSuccessMessage('');
  };

  const handleChangePage = (_, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
    }).format(amount);
  };

  const getFilteredBankPayoutData = () => {
    let filteredData = bankPayoutData;

    // Filter by payout number
    if (searchForm.payoutNo.trim()) {
      filteredData = filteredData.filter(item =>
        item.payoutNo.toLowerCase().includes(searchForm.payoutNo.toLowerCase())
      );
    }

    // Filter by member ID
    if (searchForm.memberId.trim()) {
      filteredData = filteredData.filter(item =>
        item.memberId.toLowerCase().includes(searchForm.memberId.toLowerCase())
      );
    }

    // Filter by date range
    if (searchForm.fromDate && searchForm.toDate) {
      filteredData = filteredData.filter(item => {
        const itemDate = new Date(item.paymentDate);
        const fromDate = new Date(searchForm.fromDate);
        const toDate = new Date(searchForm.toDate);
        return itemDate >= fromDate && itemDate <= toDate;
      });
    }

    return filteredData;
  };

  const getSearchSummary = () => {
    const total = getFilteredBankPayoutData().length;
    const totalNetAmount = getFilteredBankPayoutData().reduce((sum, payout) => sum + payout.netAmount, 0);
    return `Showing ${total} bank payout record(s) with total net amount of ${formatCurrency(totalNetAmount)}`;
  };

  const getStatusStyle = (status) => {
    switch (status) {
      case 'Processed':
        return { backgroundColor: '#e8f5e9', color: '#2e7d32' };
      case 'Pending':
        return { backgroundColor: '#fff3e0', color: '#f57c00' };
      case 'Failed':
        return { backgroundColor: '#ffebee', color: '#d32f2f' };
      default:
        return { backgroundColor: '#f5f5f5', color: '#666' };
    }
  };

  // Summary calculations
  const totalPayouts = bankPayoutData.length;
  const processedCount = bankPayoutData.filter(p => p.status === 'Processed').length;
  const pendingCount = bankPayoutData.filter(p => p.status === 'Pending').length;
  const failedCount = bankPayoutData.filter(p => p.status === 'Failed').length;
  const totalNetAmount = bankPayoutData.reduce((sum, payout) => sum + payout.netAmount, 0);

  const filteredData = getFilteredBankPayoutData();
  const paginatedData = filteredData.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Box sx={{ p: 3, backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#536DFE', mb: 0.5 }}>
              Payout Report for Bank
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage and view all bank payout reports
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Success Message */}
      {successMessage && (
        <Alert 
          severity="success" 
          sx={{ mb: 3 }}
          icon={<CheckCircle />}
          onClose={() => setSuccessMessage('')}
        >
          {successMessage}
        </Alert>
      )}

      {/* Summary Cards */}
      {/* <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            p: 3, 
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)', 
            borderRadius: 2,
            border: '1px solid #e0e0e0'
          }}>
            <Box display="flex" alignItems="center" gap={2}>
              <Box sx={{ 
                p: 1.5, 
                borderRadius: 2, 
                backgroundColor: '#e3f2fd' 
              }}>
                <Receipt sx={{ color: '#1976d2', fontSize: 28 }} />
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                  Total Payouts
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, color: '#1976d2', fontSize: '1.5rem' }}>
                  {totalPayouts}
                </Typography>
              </Box>
            </Box>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            p: 3, 
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)', 
            borderRadius: 2,
            border: '1px solid #e0e0e0'
          }}>
            <Box display="flex" alignItems="center" gap={2}>
              <Box sx={{ 
                p: 1.5, 
                borderRadius: 2, 
                backgroundColor: '#e8f5e9' 
              }}>
                <CheckCircle sx={{ color: '#2e7d32', fontSize: 28 }} />
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                  Processed
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, color: '#2e7d32', fontSize: '1.5rem' }}>
                  {processedCount}
                </Typography>
              </Box>
            </Box>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            p: 3, 
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)', 
            borderRadius: 2,
            border: '1px solid #e0e0e0'
          }}>
            <Box display="flex" alignItems="center" gap={2}>
              <Box sx={{ 
                p: 1.5, 
                borderRadius: 2, 
                backgroundColor: '#fff3e0' 
              }}>
                <Warning sx={{ color: '#f57c00', fontSize: 28 }} />
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                  Pending
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, color: '#f57c00', fontSize: '1.5rem' }}>
                  {pendingCount}
                </Typography>
              </Box>
            </Box>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            p: 3, 
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)', 
            borderRadius: 2,
            border: '1px solid #e0e0e0'
          }}>
            <Box display="flex" alignItems="center" gap={2}>
              <Box sx={{ 
                p: 1.5, 
                borderRadius: 2, 
                backgroundColor: '#f3e5f5' 
              }}>
                <AttachMoney sx={{ color: '#7b1fa2', fontSize: 28 }} />
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                  Total Amount
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, color: '#7b1fa2', fontSize: '1.5rem' }}>
                  ₹{(totalNetAmount / 1000).toFixed(0)}K
                </Typography>
              </Box>
            </Box>
          </Card>
        </Grid>
      </Grid> */}

      {/* Main Content Card */}
      <Card sx={{ boxShadow: '0 2px 12px rgba(0,0,0,0.1)', borderRadius: 2, mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
            Bank Payout Reports
          </Typography>
          
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="From Date"
              type="date"
              value={searchForm.fromDate}
              onChange={(e) => handleSearchFormChange('fromDate', e.target.value)}
              InputLabelProps={{ shrink: true }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <CalendarToday sx={{ color: '#666' }} />
                    </InputAdornment>
                  ),
                }}
                error={!!errors.fromDate}
                helperText={errors.fromDate}
            />
          </Grid>
          
            <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="To Date"
              type="date"
              value={searchForm.toDate}
              onChange={(e) => handleSearchFormChange('toDate', e.target.value)}
              InputLabelProps={{ shrink: true }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <CalendarToday sx={{ color: '#666' }} />
                    </InputAdornment>
                  ),
                }}
                error={!!errors.toDate}
                helperText={errors.toDate}
            />
          </Grid>
          
            <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="Payout No"
              value={searchForm.payoutNo}
              onChange={(e) => handleSearchFormChange('payoutNo', e.target.value)}
              placeholder="Enter payout number"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Receipt sx={{ color: '#666' }} />
                    </InputAdornment>
                  ),
                }}
            />
          </Grid>
          
            <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="Member ID"
              value={searchForm.memberId}
              onChange={(e) => handleSearchFormChange('memberId', e.target.value)}
              placeholder="Enter member ID"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Person sx={{ color: '#666' }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          </Grid>
          
          <Box display="flex" gap={2} mb={3}>
              <Button
                variant="contained"
              startIcon={<Search />}
                onClick={handleSearch}
              sx={{ 
                textTransform: 'none',
                fontWeight: 600
              }}
            >
              Generate Report
              </Button>
              
              <Button
                variant="outlined"
              startIcon={<Refresh />}
                onClick={handleReset}
              sx={{ 
                textTransform: 'none',
                fontWeight: 600
              }}
              >
                Reset
              </Button>
            </Box>

          {/* Search Summary */}
          {/* {filteredData.length > 0 && (
            <Alert 
              severity="info" 
              icon={<Info />}
              sx={{ mb: 3 }}
            >
              {getSearchSummary()}
            </Alert>
          )} */}

          {/* Results Table */}
          {filteredData.length > 0 ? (
            <>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#536DFE' }}>
                      <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>
                        Payout Info
                      </TableCell>
                      <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>
                        Member Details
                      </TableCell>
                      <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>
                        Bank Details
                      </TableCell>
                      <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>
                        Financial Details
                      </TableCell>
                      <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>
                        Status
                      </TableCell>
                      <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>
                        Actions
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedData.map((payout) => (
                      <TableRow 
                        key={payout.id}
                        hover
                        sx={{
                          '&:nth-of-type(odd)': { backgroundColor: '#f9f9f9' },
                          '&:hover': { backgroundColor: '#f0f7ff !important' }
                        }}
                      >
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Receipt sx={{ fontSize: 16, color: '#1976d2' }} />
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {payout.payoutNo}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Date: {payout.paymentDate}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Person sx={{ fontSize: 16, color: '#2e7d32' }} />
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {payout.memberName}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                ID: {payout.memberId}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            <AccountBalance sx={{ fontSize: 16, color: '#f57c00' }} />
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {payout.bankName}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                A/C: {payout.accountNumber}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" display="block">
                                IFSC: {payout.ifscCode}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            <AttachMoney sx={{ fontSize: 16, color: '#7b1fa2' }} />
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 600, color: '#7b1fa2' }}>
                                Net: {formatCurrency(payout.netAmount)}
        </Typography>
                              <Typography variant="caption" color="text.secondary">
                                TDS: {formatCurrency(payout.tdsAmount)}
          </Typography>
                              <Typography variant="caption" color="text.secondary" display="block">
                                Gross: {formatCurrency(payout.grossAmount)}
          </Typography>
        </Box>
                          </Box>
                        </TableCell>
                        
                        <TableCell>
                          <Chip
                            label={payout.status}
                            sx={getStatusStyle(payout.status)}
                            size="small"
                          />
                        </TableCell>
                        
                        <TableCell>
                          <Box display="flex" gap={0.5}>
                            <Tooltip title="Download Report">
                              <IconButton 
                                size="small"
                                sx={{ border: '1px solid #e0e0e0' }}
                              >
                                <Download fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            
                            <Tooltip title="Print Report">
                              <IconButton 
                                size="small"
                                sx={{ border: '1px solid #e0e0e0' }}
                              >
                                <Print fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Pagination */}
              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={filteredData.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                sx={{ 
                  borderTop: '1px solid #e0e0e0',
                  mt: 2
                }}
              />
            </>
          ) : (
            <Alert 
              severity="warning" 
              icon={<Warning />}
              sx={{ mt: 3 }}
            >
              No bank payout records found. Please adjust your search criteria and try again.
            </Alert>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default PayoutReportForBank;